import { useState } from 'react';
import { Layers, MousePointer2, Route } from 'lucide-react';
import { useSettings } from '@/store/settings';
import { cn } from '@/lib/utils';

/**
 * View options — a 29px icon stack sitting directly below the MapLibre
 * NavigationControl column (+ / − / compass). Clicking the Layers button
 * slides the Trail square down (GLOBAL breadcrumb visibility — all assets).
 * Per-asset follow / fly-to / trail live on the asset rows in the sidebar
 * (operator call 2026-07-14: map holds only whole-map options).
 */
export function ViewOptions() {
  const [open, setOpen] = useState(false);
  const showTrails = useSettings((s) => s.map.showTrails !== false);
  const showHoverInfo = useSettings((s) => s.map.showHoverInfo !== false);
  const setMap = useSettings((s) => s.setMap);

  const anyOn = showTrails || showHoverInfo;

  return (
    <div className="absolute right-[10px] top-[110px] z-10 flex w-[29px] flex-col">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="View options — trails (all assets)"
        className={cn(
          'relative grid h-[29px] w-[29px] place-items-center rounded border backdrop-blur-md transition',
          open
            ? 'border-accent bg-accent/15 text-accent'
            : 'border-border-default bg-base/85 text-fg-secondary hover:text-fg-primary',
        )}
      >
        <Layers className="h-3.5 w-3.5" />
        {anyOn && !open && (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full"
            style={{ background: 'var(--color-accent)' }}
          />
        )}
      </button>

      {/* Slide-down stack — max-height animates so the squares unfold
          beneath the button; width never exceeds the 29px column. */}
      <div
        className={cn(
          'flex flex-col gap-1 overflow-hidden transition-all duration-200 ease-out',
          open ? 'mt-1 max-h-24 opacity-100' : 'max-h-0 opacity-0',
        )}
        aria-hidden={!open}
      >
        <StackButton
          label={showTrails ? 'Trails on (all assets) — click to hide' : 'Show robot trails (all assets)'}
          active={showTrails}
          onClick={() => setMap({ showTrails: !showTrails })}
        >
          <Route className="h-3.5 w-3.5" />
        </StackButton>
        <StackButton
          label={
            showHoverInfo
              ? 'Cursor info on (lat/lon + distance) — click to hide'
              : 'Show cursor info (lat/lon + distance)'
          }
          active={showHoverInfo}
          onClick={() => setMap({ showHoverInfo: !showHoverInfo })}
        >
          <MousePointer2 className="h-3.5 w-3.5" />
        </StackButton>
      </div>
    </div>
  );
}

function StackButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'grid h-[29px] w-[29px] place-items-center rounded border backdrop-blur-md transition',
        active
          ? 'border-accent bg-accent/15 text-accent'
          : 'border-border-default bg-base/85 text-fg-secondary hover:text-fg-primary',
        disabled && 'cursor-not-allowed opacity-40 hover:text-fg-secondary',
      )}
    >
      {children}
    </button>
  );
}
